
import { motion } from 'framer-motion';
import { useState } from 'react';
import Button from '../../../components/base/Button';

interface PopupData {
  title: string;
  description: string;
  details: string[];
}

export default function HeroSection() {
  const [selectedPopup, setSelectedPopup] = useState<PopupData | null>(null);

  const popupData: Record<string, PopupData> = {
    years: {
      title: "59年の信頼と実績",
      description: "1965年創業から培った確かな実績",
      details: [
        "1965年に群馬県で創業、地域密着型の広告会社として歩んできました",
        "地元企業様から大手企業様まで、幅広いクライアント様との長期的なパートナーシップ",
        "時代の変化に対応しながら、常に最新の広告手法を取り入れてきた実績",
        "世代を超えて信頼いただいているクライアント様も多数"
      ]
    },
    projects: {
      title: "実績",
      description: "多種多様な業界での豊富な実績",
      details: [
        "製造業、サービス業、医療・福祉、教育機関など幅広い業界での実績",
        "テレビCM、ラジオCM、新聞広告、Web広告まで多様なメディア展開",
        "ブランディングから販促まで、総合的なマーケティング支援",
        "地域イベントから全国規模のキャンペーンまで対応"
      ]
    },
    ai: {
      title: "AI最新技術導入",
      description: "先進技術を活用した効果的な広告戦略",
      details: [
        "AI解析による効果的なターゲティング広告の実現",
        "機械学習を活用したクリエイティブ最適化",
        "データドリブンなマーケティング戦略の立案・実行",
        "最新のマーケティングオートメーションツールの導入・運用"
      ]
    },
    comprehensive: {
      title: "総合力",
      description: "新日東グループとしての総合サポート力",
      details: [
        "グループ企業との連携による建設・施工から広告まで一貫したサービス",
        "設計・施工・広告・メンテナンスまでワンストップで対応可能",
        "グループ全体の豊富な経験と技術力を活用したトータルソリューション",
        "複合的なプロジェクトにも柔軟に対応できる組織力とネットワーク"
      ]
    }
  };

  const openPopup = (key: string) => {
    setSelectedPopup(popupData[key]);
  };

  const closePopup = () => {
    setSelectedPopup(null);
  };

  return (
    <section 
      id="home" 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%), url('https://public.readdy.ai/ai/img_res/d80e481b28540d50820fb6c9057ff8c5.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-purple-900/20"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
              style={{ textShadow: '2px 2px 4px rgba(255, 255, 255, 0.8), -1px -1px 2px rgba(255, 255, 255, 0.8), 1px -1px 2px rgba(255, 255, 255, 0.8), -1px 1px 2px rgba(255, 255, 255, 0.8)' }}
            >
              信頼と実績の<br />
              <span className="text-blue-600">広告会社</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-xl text-gray-700 mb-8 leading-relaxed"
              style={{ textShadow: '1px 1px 2px rgba(255, 255, 255, 0.8), -1px -1px 1px rgba(255, 255, 255, 0.8), 1px -1px 1px rgba(255, 255, 255, 0.8), -1px 1px 1px rgba(255, 255, 255, 0.8)' }}
            >
              1965年創業、群馬の老舗広告会社として培った信頼と実績。<br />
              新日東グループの総合力で、企業の困りごとにお応えします。
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button 
                size="lg" 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                お問い合わせ
              </Button>
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              >
                サービス一覧
              </Button>
            </motion.div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
              <div className="grid grid-cols-2 gap-6 text-center">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/20 rounded-xl p-4 cursor-pointer"
                  onClick={() => openPopup('years')}
                >
                  <div className="text-3xl font-bold text-blue-600 mb-2">59年</div>
                  <div className="text-sm text-gray-700">の信頼と実績</div>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/20 rounded-xl p-4 cursor-pointer"
                  onClick={() => openPopup('projects')}
                >
                  <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                  <div className="text-sm text-gray-700">実績</div>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/20 rounded-xl p-4 cursor-pointer"
                  onClick={() => openPopup('ai')}
                >
                  <div className="text-3xl font-bold text-blue-600 mb-2">AI</div>
                  <div className="text-sm text-gray-700">最新技術導入</div>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/20 rounded-xl p-4 cursor-pointer"
                  onClick={() => openPopup('comprehensive')}
                >
                  <div className="text-3xl font-bold text-blue-600 mb-2">総合力</div>
                  <div className="text-sm text-gray-700">グループ連携</div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ポップアップモーダル */}
      {selectedPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closePopup}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedPopup.title}
                </h3>
                <p className="text-lg text-blue-600 font-medium">
                  {selectedPopup.description}
                </p>
              </div>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              {selectedPopup.details.map((detail, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start space-x-3"
                >
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-blue-600 text-sm"></i>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{detail}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              <Button onClick={closePopup}>
                閉じる
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
}
